/* ------------------------------------------------------------------
   Central Android WebView bridge
   ------------------------------------------------------------------ */

const W = typeof window === 'undefined' ? {} : window
const A = typeof Android === 'undefined' ? undefined : Android

/* ---------- ENV checks ------------------------------------------------ */

export const isAndroid = () => Boolean(A)
export const isModernWebView = () => isAndroid() && !!A.getBasicPayloadForAdvertiser
export const isLegacyNewsWebView = () => isAndroid() && !isModernWebView()

/* ---------- Basic payload -------------------------------------------- */

let cachedBasic = null

export const getBasicPayload = () => {
  if (!isModernWebView()) return null
  if (cachedBasic) return cachedBasic
  try {
    cachedBasic = JSON.parse(A.getBasicPayloadForAdvertiser() || '{}')
    return cachedBasic
  } catch (err) {
    console.error('[bridge] failed to parse basic payload', err)
    return null
  }
}

/* ---------- Derived helpers ------------------------------------------ */

export const getAppType = () => {
  if (!isAndroid()) return 'web'
  /* ----- 1️⃣  legacy News WebView (no getBasicPayloadForAdvertiser) ----- */
  if (isLegacyNewsWebView()) return 'news'

  /* ----- 2️⃣  modern SDK-based WebViews (Jobs / News) ------------------- */
  const basic = getBasicPayload()
  if (basic?.app_type === 'jobs_app') return 'jobs_wbv'
  if (basic?.app_type === 'news_app') return 'news_wbv'

  /* ----- fallback (shouldn’t really happen) ---------------------------- */
  return 'web'
}

export const getAcceptLanguage = () => getBasicPayload()?.preferred_locale || 'en'

export const getSdkVersion = () => getBasicPayload()?.['x-sdk-version']
export const getAppVersion = () => getBasicPayload()?.['x-app-version']
export const getAndroidUserId = () => A?.getAndroidUserId?.() ?? null
export const getAdvertiserContact = () => A?.getAdvertiserContact?.() ?? null

/* ---------- Tokens ---------------------------------------------------- */

export const getRawUserToken = () => A?.getUserToken?.() ?? null

/* ---------- One-shot toast / exit helpers ---------------------------- */

export const showAndroidToast = (msg = '') => A?.showToast?.(String(msg))
export const exitApp = () => A?.exitApp?.()

/* ---------- Back-gesture hook (Jobs app) ----------------------------- */

export const registerBackGesture = (fn = () => {}) => {
  if (!isAndroid()) return
  W.backGestureNavigationJobsApp = fn
}

/* ---------- Leave-page (legacy) ------------------------------------- */

export const leavePage = (doReload = false) => A?.leavePage?.(doReload)

/* ---------- Android → JS callbacks registrars ----------------------- */

export const registerLocationCallback = (fn) => {
  if (!isAndroid()) return
  W.onLocationCoordinatesReceived = fn
}

export const registerAndroidBackNav = (fn) => {
  if (!isAndroid()) return
  W.backGestureNavigationJobsApp = fn
}

/* ---------- App-specific logout intents ---------------------------- */

export const androidLogout = () => isAndroid() && A?.logout?.() // Jobs app
export const androidCloseWebForm = () => isAndroid() && A?.closeWebForm?.() // News app

export const getLegacyBasicDetails = () => {
  if (!isLegacyNewsWebView() || typeof A?.getBasicDetailsPayload !== 'function') return null;

  try {
    const raw = A?.getBasicDetailsPayload?.();
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('[bridge] failed to parse legacy basic details payload', err);
    return null;
  }
};

/* =====================================================================
     DOWNLOADS & OTHER NATIVE INTENTS
   ===================================================================== */

/**
 * Trigger Android-side downloader.
 * NOTE: `fileName` **must include the extension** (e.g. "sample.pdf").
 */
export const downloadFile = (url = '', fileName = '') => {
  if (!isAndroid() || !url) return;
  // Pass null if caller didn’t supply a name – native code expects it.
  A?.downloadFile?.(String(url), fileName || null);
};

/** Open the phone dial-pad with the number pre-filled. */
export const callNumber = (number = '') => {
  if (!isAndroid() || !number) return;
  A?.callNumber?.(String(number));
};

/**
 * Fire WhatsApp share intent with pre-populated text & link.
 * Both arguments are required by native signature.
 */
export const shareWhatsapp = (text = '', link = '') => {
  if (!isAndroid() || !(text || link)) return;
  A?.shareWhatsapp?.(String(text), String(link));
};

/**
 * Open the user’s e-mail client.
 * `email` (recipient) is mandatory; `subject` & `body` are optional.
 */
export const openEmail = (
  email   = '',
  subject = '',
  body    = '',
) => {
  if (!isAndroid() || !email) return;
  A?.openEmailApp?.(String(email), subject || null, body || null);
};

/**
 * Download a base-64 encoded file through the native layer.
 * @param {string} dataUrl   MUST start with "data:…;base64,"
 * @param {string} fileName  e.g. "candidates.csv"
 */
export const downloadBase64File = (dataUrl = '', fileName = '') => {
  if (!isAndroid() || !dataUrl) return;
  Android?.downloadBase64File?.(dataUrl, fileName || null);
};

export const pushJwtToAndroid = (tokens = {}) => {
  if (!isModernWebView() || typeof A?.updateJwtToken !== 'function') return;
  if (!tokens || !tokens.access) return;

  try {
    A.updateJwtToken(JSON.stringify(tokens));
  } catch (err) {
    console.error('[bridge] updateJwtToken failed', err);
  }
};
